import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { httpClient } from "@/core/api/http-client";
import { apiErrorMessage } from "@/core/api/http-client";
import {
  describePasswordRule,
  isPasswordAcceptable,
} from "@/features/user-management/initial-password";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { toast } from "@/shared/components/ui/toast";

/**
 * A user replacing the password an administrator handed them.
 *
 * Until now every change went through an administrator, so staff kept the slip
 * of paper they were given and an SSE fielded the requests forever. The
 * current password is asked for even though the user is signed in: a tablet
 * left open on the shop floor should not be enough to take an account over.
 */
export function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Nothing typed should survive a close: the next person to open it is not
  // necessarily the person who opened it last.
  useEffect(() => {
    if (!open) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      setSaving(false);
    }
  }, [open]);

  const submit = async () => {
    if (!current) {
      setError("Enter your current password");
      return;
    }
    if (!isPasswordAcceptable(next)) {
      setError(describePasswordRule());
      return;
    }
    if (next === current) {
      setError("The new password must be different from the current one");
      return;
    }
    if (next !== confirm) {
      setError("The two new passwords do not match");
      return;
    }

    setSaving(true);
    try {
      await httpClient.post<void>("/auth/change-password", {
        currentPassword: current,
        newPassword: next,
      });
      toast.success("Password changed — use it next time you sign in");
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not change the password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            Choose a password only you know. It replaces the one you were given and does not expire.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => {
                setCurrent(e.target.value);
                if (error) setError(null);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              placeholder={describePasswordRule()}
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
                if (error) setError(null);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (error) setError(null);
              }}
            />
          </div>

          {error && (
            <p role="alert" className="text-xs text-red-600">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => void submit()}
            disabled={saving}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Change password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
